/**
 * API Endpoint Resolution
 *
 * Resolves API paths to full URLs based on environment:
 * - Development: Returns path as-is (Vite proxy forwards to local Hono server)
 * - Staging/Production: Prepends the API Gateway base URL (never the Cloud Run
 *   URL — the service is `--no-allow-unauthenticated`)
 *
 * The API key is sent via the `X-API-Key` header (see {@link getApiAuthHeaders})
 * AND, transitionally, the `?key=` query parameter. The query form must stay
 * until the API Gateway's ESPv2 spec (api-gateway-config.*.yaml) is switched
 * from `in: query` to `in: header` key validation — ESPv2 rejects requests
 * missing the declared key location and cannot accept both. Once the gateway
 * validates the header, remove the query append here so keys stop leaking
 * into browser history, proxy/access logs, and `Referer` headers.
 *
 * Environment variables:
 * - VITE_API_BASE_URL: API Gateway URL (e.g., https://dialpad-webhook-gateway-xxx.uc.gateway.dev)
 * - VITE_API_KEY: API key for authenticating with the Hono server
 */

/**
 * Resolve an API path to a full endpoint URL.
 *
 * @param path - API path (e.g., "/api/v1/sms/send")
 * @returns Full URL in staging/production, or relative path in development
 * @agentic connections
 */
export const getApiEndpoint = (path: string): string => {
  if (import.meta.env.MODE === "development") {
    return path;
  }

  const baseUrl = import.meta.env.VITE_API_BASE_URL;

  if (!baseUrl) {
    console.error(
      "VITE_API_BASE_URL is not configured — API calls will fail in this environment",
    );
    return path;
  }

  const apiKey = import.meta.env.VITE_API_KEY;
  if (!apiKey) {
    return `${baseUrl}${path}`;
  }

  // TRANSITIONAL: the API Gateway still validates `?key=` (see module doc).
  const separator = path.includes("?") ? "&" : "?";
  return `${baseUrl}${path}${separator}key=${apiKey}`;
};

/**
 * Authentication headers for backend API calls. Returns the `X-API-Key`
 * header in staging/production; empty in development, where the Vite proxy
 * injects the key server-side. Spread into every `fetch` alongside
 * `getApiEndpoint`.
 * @agentic connections
 */
export const getApiAuthHeaders = (): Record<string, string> => {
  if (import.meta.env.MODE === "development") {
    return {};
  }

  const apiKey = import.meta.env.VITE_API_KEY;
  return apiKey ? { "X-API-Key": apiKey } : {};
};
